/**
 * ResendCreateModal — extracted from Chat.jsx (Feb 2026). Admin form
 * to create a new re-dispatch task from arbitrary LPG data. Business
 * logic UNCHANGED — pure code move.
 */
import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

function ResendCreateModal({ onClose }) {
  const [agencyName, setAgencyName] = useState("");
  const [lpgId, setLpgId] = useState("");
  const [dac, setDac] = useState("");
  const [mobile, setMobile] = useState("");
  const [reason, setReason] = useState("");
  const [match, setMatch] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [needsConfirm, setNeedsConfirm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  useEffect(() => {
    setMatch(null);
    setErr("");
    setNeedsConfirm(null);
    const name = agencyName.trim();
    if (name.length < 2) return;
    const t = setTimeout(async () => {
      setResolving(true);
      try {
        const { data } = await api.post("/resend/resolve", {
          agency_name: name,
        });
        setMatch(data);
      } catch (_e) {
        setMatch(null);
      } finally {
        setResolving(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [agencyName]);

  const canSubmit =
    agencyName.trim().length >= 2 &&
    /^\d{4,20}$/.test(lpgId.trim()) &&
    !submitting;

  const doSubmit = async (force = false) => {
    setErr("");
    setOk("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/resend/entries", {
        agency_name: agencyName.trim(),
        lpg_id: lpgId.trim(),
        dac: dac.trim(),
        mobile: mobile.trim(),
        reason: reason.trim(),
        force,
      });
      if (data.needs_confirm) {
        setNeedsConfirm({
          warning: data.warning,
          suggested: data.suggested_group_name,
        });
        return;
      }
      setOk(
        `Submitted — agency: ${data.entry.agency_resolved}. Sheet + chat updated.`,
      );
      setLpgId("");
      setDac("");
      setMobile("");
      setReason("");
      setNeedsConfirm(null);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
      data-testid="resend-create-modal"
    >
      <div className="bg-[#F5F5F0] rounded-lg shadow-xl w-full max-w-lg flex flex-col">
        <div className="px-5 py-4 border-b border-[#DCDCD3] flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#8A6046]">
              Resend DAC · New Request
            </div>
            <div className="text-lg font-semibold text-[#1C1C19]">
              File a resend ticket
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="resend-create-close"
            className="text-[#6B6B61] hover:text-[#1C1C19] p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-auto" style={{ maxHeight: "70vh" }}>
          <div>
            <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] block mb-1">
              Agency name *
            </label>
            <input
              value={agencyName}
              onChange={(e) => setAgencyName(e.target.value)}
              placeholder="e.g. FAIRWAYS HPCL"
              data-testid="resend-create-agency"
              className="w-full px-3 py-2 border border-[#DCDCD3] rounded text-sm focus:outline-none focus:border-[#385C4B] bg-white"
            />
            {resolving && (
              <p className="text-[10px] text-[#6B6B61] mt-1 font-mono">
                checking…
              </p>
            )}
            {!resolving && match && match.group_id && (
              <p
                className={`text-[11px] mt-1 font-mono ${
                  match.exact ? "text-emerald-700" : "text-amber-700"
                }`}
                data-testid="resend-create-match"
              >
                {match.exact ? "✓ matches" : "⚠ closest match"}: {match.group_name}
                {!match.exact && match.warning ? ` — ${match.warning}` : ""}
              </p>
            )}
            {!resolving && match && !match.group_id && agencyName.trim().length >= 2 && (
              <p className="text-[11px] text-red-700 mt-1 font-mono">
                ✗ no agency matched — check spelling
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] block mb-1">
                LPG ID *
              </label>
              <input
                value={lpgId}
                onChange={(e) =>
                  setLpgId(e.target.value.replace(/\D/g, "").slice(0, 20))
                }
                placeholder="6–17 digits"
                inputMode="numeric"
                data-testid="resend-create-lpg"
                className="w-full px-3 py-2 border border-[#DCDCD3] rounded text-sm focus:outline-none focus:border-[#385C4B] bg-white font-mono"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] block mb-1">
                DAC (optional)
              </label>
              <input
                value={dac}
                onChange={(e) =>
                  setDac(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="4-digit code"
                inputMode="numeric"
                data-testid="resend-create-dac"
                className="w-full px-3 py-2 border border-[#DCDCD3] rounded text-sm focus:outline-none focus:border-[#385C4B] bg-white font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] block mb-1">
                Mobile (optional)
              </label>
              <input
                value={mobile}
                onChange={(e) =>
                  setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                }
                placeholder="10 digits"
                inputMode="numeric"
                data-testid="resend-create-mobile"
                className="w-full px-3 py-2 border border-[#DCDCD3] rounded text-sm focus:outline-none focus:border-[#385C4B] bg-white font-mono"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] block mb-1">
                Reason (optional)
              </label>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 80))}
                placeholder="e.g. DAC not received"
                data-testid="resend-create-reason"
                className="w-full px-3 py-2 border border-[#DCDCD3] rounded text-sm focus:outline-none focus:border-[#385C4B] bg-white"
              />
            </div>
          </div>

          {needsConfirm && (
            <div
              className="rounded border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="resend-create-needs-confirm"
            >
              <div className="font-semibold mb-1">Spelling mismatch warning</div>
              <div className="mb-2">{needsConfirm.warning}</div>
              <button
                onClick={() => doSubmit(true)}
                disabled={submitting}
                data-testid="resend-create-force-submit"
                className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded text-xs font-semibold"
              >
                {submitting ? "Submitting…" : `Yes, send to ${needsConfirm.suggested}`}
              </button>
            </div>
          )}

          {err && (
            <div
              className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-800"
              data-testid="resend-create-error"
            >
              {err}
            </div>
          )}
          {ok && (
            <div
              className="rounded border border-emerald-300 bg-emerald-50 p-2 text-sm text-emerald-800"
              data-testid="resend-create-success"
            >
              {ok}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#DCDCD3] flex justify-end gap-2 bg-white rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-[#DCDCD3] rounded hover:bg-[#F5F5F0]"
            data-testid="resend-create-cancel"
          >
            Close
          </button>
          <button
            onClick={() => doSubmit(false)}
            disabled={!canSubmit}
            data-testid="resend-create-submit"
            className="px-4 py-2 text-sm bg-[#385C4B] text-white rounded hover:bg-[#2d4a3c] disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            {submitting ? "Submitting…" : "Submit Resend"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResendCreateModal;
